import React, { useState, useEffect, useMemo, useRef } from "react";
import StoreSidebar from "../components/StoreSidebar";
import { motion, AnimatePresence } from "framer-motion";
import { db } from "../firebase/firebase";
import { collection, onSnapshot, query, where, doc, setDoc } from "firebase/firestore";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import {
  Package, Search, Download, Layers,
  DollarSign, ShoppingCart, Calendar as CalendarIcon, 
  X, Filter, Tag, LayoutGrid, ChevronLeft, ChevronRight, 
  AlertTriangle, Eye, ChevronDown
} from "lucide-react";
import { PieChart, Pie, Cell, Tooltip as RechartsTooltip, Legend, ResponsiveContainer } from "recharts";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#eab308', '#ec4899'];

export default function StoreClosingStock() {
  const [activeBranchId, setActiveBranchId] = useState("");
  const [storeName, setStoreName] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isDataLoading, setIsDataLoading] = useState(true);

  const [inventory, setInventory] = useState([]);
  const [sales, setSales] = useState([]);

  const [search, setSearch] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");

  const [showPdfMenu, setShowPdfMenu] = useState(false);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);
  const [isViewAllOpen, setIsViewAllOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const auth = getAuth();
    let unsubStore = null;

    const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
      if (user) {
        setIsDataLoading(true);
        const storeQuery = query(collection(db, "stores"), where("linkedAuthUid", "==", user.uid));
        
        unsubStore = onSnapshot(storeQuery, (snapshot) => {
          if (!snapshot.empty) {
            const storeDoc = snapshot.docs[0];
            setActiveBranchId(storeDoc.id);
            setStoreName(storeDoc.data().storeId || storeDoc.data().name || "Authorized Branch");
            setErrorMessage("");
          } else {
            setErrorMessage("Unauthorized Access: No assigned branch profile found.");
            setIsDataLoading(false);
          }
        }, () => {
          setErrorMessage("Failed to read branch configuration mapping database.");
          setIsDataLoading(false);
        });
      } else {
        setIsDataLoading(false);
      }
    });

    return () => {
      unsubscribeAuth();
      if (unsubStore) unsubStore();
    };
  }, []);

  useEffect(() => {
    if (!activeBranchId) return;

    const unsubInventory = onSnapshot(collection(db, "stores", activeBranchId, "inventory"), (snapshot) => {
      setInventory(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      setIsDataLoading(false);
    });

    const unsubSales = onSnapshot(collection(db, "stores", activeBranchId, "sales"), (snapshot) => {
      setSales(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });

    return () => { unsubInventory(); unsubSales(); };
  }, [activeBranchId]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowPdfMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const uniqueTypes = useMemo(() => Array.from(new Set(inventory.map(i => i.productType).filter(Boolean))), [inventory]);
  const uniqueCategories = useMemo(() => Array.from(new Set(inventory.map(i => i.category).filter(Boolean))), [inventory]);

  const processedInventory = useMemo(() => {
    const salesMap = {};

    sales.forEach(sale => {
      if (sale.timestamp) {
        const saleDate = sale.timestamp.split("T")[0];
        if (startDate && saleDate < startDate) return;
        if (endDate && saleDate > endDate) return;
      }
      if (sale.items && Array.isArray(sale.items)) {
        sale.items.forEach(item => {
          const code = item.productCode?.toLowerCase();
          if (code) {
            salesMap[code] = (salesMap[code] || 0) + (Number(item.quantity) || 0);
          }
        });
      }
    });

    let filtered = inventory.map(item => ({
      ...item,
      soldQuantity: salesMap[item.productCode?.toLowerCase()] || 0
    }));

    if (search.trim()) {
      const s = search.toLowerCase();
      filtered = filtered.filter(item => 
        (item.productName || "").toLowerCase().includes(s) || 
        (item.productCode || "").toLowerCase().includes(s)
      );
    }
    if (typeFilter) filtered = filtered.filter(item => item.productType === typeFilter);
    if (categoryFilter) filtered = filtered.filter(item => item.category === categoryFilter);

    return filtered;
  }, [inventory, sales, search, startDate, endDate, typeFilter, categoryFilter]);

  const summaryStats = useMemo(() => {
    let totalStock = 0, totalSold = 0, totalValuation = 0, uniqueSKUs = new Set();
    const typeSummaryMap = {};
    const chartDataMap = {};

    processedInventory.forEach(item => {
      const q = Number(item.quantity) || 0;
      const s = Number(item.soldQuantity) || 0;
      const p = Number(item.retailPrice || item.unitPrice || item.purchasePrice || 0);
      const val = q * p;
      const type = item.productType || "Uncategorized";

      totalStock += q;
      totalSold += s;
      totalValuation += val;
      if (item.productCode) uniqueSKUs.add(item.productCode);

      if (!typeSummaryMap[type]) typeSummaryMap[type] = { quantity: 0, valuation: 0 };
      typeSummaryMap[type].quantity += q;
      typeSummaryMap[type].valuation += val;

      if (q > 0) chartDataMap[type] = (chartDataMap[type] || 0) + q;
    });

    const typeSummaryArray = Object.entries(typeSummaryMap).map(([type, data]) => ({ type, ...data }));
    const chartDataArray = Object.entries(chartDataMap).map(([name, value]) => ({ name, value }));

    return { totalStock, totalSold, totalValuation, uniqueSKUCount: uniqueSKUs.size, typeSummaryArray, chartDataArray };
  }, [processedInventory]);

  const displayData = useMemo(() => {
    return processedInventory.slice(0, 6);
  }, [processedInventory]);

  const saveClosingReportToFirestore = async () => {
    if (!activeBranchId || processedInventory.length === 0) return;
    
    try {
      const now = new Date();
      const istDateString = new Intl.DateTimeFormat('en-CA', { 
        timeZone: 'Asia/Kolkata', 
        year: 'numeric', 
        month: '2-digit', 
        day: '2-digit' 
      }).format(now);

      const istHour = parseInt(new Intl.DateTimeFormat('en-US', {
        timeZone: 'Asia/Kolkata',
        hour: 'numeric',
        hour12: false
      }).format(now), 10);

      const shiftName = istHour < 16 ? "Morning_Shift" : "Closing_Shift";
      const documentId = `${istDateString}_${shiftName}`;

      const reportRef = doc(db, "stores", activeBranchId, "closing-stock", documentId);

      const reportPayload = {
        date: istDateString,
        shift: shiftName,
        soldStock: summaryStats.totalSold,
        closingStockBalance: summaryStats.totalStock,
        stockValuation: summaryStats.totalValuation,
        productTypeSummary: summaryStats.typeSummaryArray,
        masterStockLedger: processedInventory.map(item => ({
          productCode: item.productCode || item.sku || "N/A",
          productName: item.productName || "Unnamed",
          productType: item.productType || "-",
          soldQuantity: item.soldQuantity || 0,
          closingQuantity: Number(item.quantity) || 0,
          unitPrice: Number(item.retailPrice || item.unitPrice || item.purchasePrice || 0),
          totalValuation: (Number(item.quantity) || 0) * Number(item.retailPrice || item.unitPrice || item.purchasePrice || 0)
        })),
        lastSyncTimestamp: now.toISOString()
      };

      await setDoc(reportRef, reportPayload, { merge: true });
    } catch (error) {
      console.error("Error saving closing report to Firestore:", error);
    }
  };

  const handlePDF = async (action = "download") => {
    setShowPdfMenu(false); 
    
    if (action === "download") {
      await saveClosingReportToFirestore();
    }

    // STRICTLY HALF OF A4 PORTRAIT (148.5mm x 210mm)
    // Using an exact array bypasses any browser/library defaults for "A4"
    const docPdf = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: [148.5, 210] 
    });
    
    const pageWidth = docPdf.internal.pageSize.getWidth(); // Exactly 148.5
    const timestampString = new Date().toLocaleString();
    
    const drawHeader = (data) => {
      if (data.pageNumber === 1) {
        docPdf.setFillColor(30, 41, 59);
        docPdf.rect(0, 0, pageWidth, 20, "F");
        docPdf.setFont("helvetica", "bold");
        docPdf.setFontSize(12); 
        docPdf.setTextColor(255, 255, 255);
        docPdf.text(`SHRUTI ERP - CLOSING STOCK`, 10, 10);
        
        docPdf.setFontSize(7);
        docPdf.setFont("helvetica", "normal");
        docPdf.setTextColor(148, 163, 184);
        docPdf.text(`Branch: ${storeName.toUpperCase()}`, 10, 15);
        
        docPdf.setTextColor(255, 255, 255);
        docPdf.text(`Generated: ${timestampString}`, pageWidth - 10, 10, { align: "right" });
        if (startDate || endDate) {
          docPdf.setTextColor(16, 185, 129);
          docPdf.text(`Filter: ${startDate || 'Start'} to ${endDate || 'End'}`, pageWidth - 10, 15, { align: "right" });
        }

        docPdf.setFillColor(37, 99, 235);
        docPdf.rect(0, 20, pageWidth, 1.5, "F");
      } else {
        docPdf.setFontSize(7);
        docPdf.setTextColor(100, 100, 100);
        docPdf.text(`Closing Stock Ledger - Page ${data.pageNumber}`, 10, 10);
      }
    };

    const kpiRows = [
      ["Active SKUs", String(summaryStats.uniqueSKUCount)],
      ["Sold Units", String(summaryStats.totalSold)],
      ["Closing Stock", `${summaryStats.totalStock.toLocaleString()}`],
      ["Stock Valuation", `Rs. ${summaryStats.totalValuation.toLocaleString()}`]
    ];

    autoTable(docPdf, {
      startY: 25,
      head: [["Metric", "Value"]],
      body: kpiRows,
      theme: "grid",
      tableWidth: 80, 
      styles: { fontSize: 7, cellPadding: 2, textColor: [30, 41, 59] },
      headStyles: { fillColor: [16, 185, 129], fontStyle: "bold" },
      margin: { top: 15, right: 10, bottom: 20, left: 10 }, // Hard bottom margin
      rowPageBreak: 'avoid',
      didDrawPage: drawHeader
    });

    let activeY = Math.max(docPdf.lastAutoTable.finalY + 8, 55);

    docPdf.setFont("helvetica", "bold");
    docPdf.setFontSize(9);
    docPdf.setTextColor(30, 41, 59);
    docPdf.text("TYPE SUMMARY", 10, activeY);
    
    const typeRowsPDF = summaryStats.typeSummaryArray.map(item => [
      item.type,
      `${item.quantity.toLocaleString()}`,
      `Rs. ${item.valuation.toLocaleString()}`
    ]);

    autoTable(docPdf, {
      startY: activeY + 3,
      head: [["Type", "Qty", "Valuation"]],
      body: typeRowsPDF.length > 0 ? typeRowsPDF : [["-", "-", "-"]],
      theme: "striped",
      styles: { fontSize: 7, cellPadding: 2 },
      headStyles: { fillColor: [37, 99, 235] },
      margin: { top: 15, right: 10, bottom: 20, left: 10 }, // Hard bottom margin
      rowPageBreak: 'avoid'
    });

    let ledgerStartY = docPdf.lastAutoTable.finalY + 12;

    // Strict boundary check before starting the ledger table
    // If the Y position is greater than 170mm (on a 210mm page), force a completely new page.
    if (ledgerStartY > 170) { 
      docPdf.addPage();
      ledgerStartY = 15;
    }

    docPdf.setFillColor(30, 41, 59);
    docPdf.rect(0, ledgerStartY - 6, pageWidth, 10, "F");
    docPdf.setFont("helvetica", "bold");
    docPdf.setFontSize(9);
    docPdf.setTextColor(255, 255, 255);
    docPdf.text(`MASTER STOCK LEDGER`, 10, ledgerStartY + 1);

    const ledgerRows = processedInventory.map(item => {
      const q = Number(item.quantity) || 0;
      const s = Number(item.soldQuantity) || 0;
      const p = Number(item.retailPrice || item.unitPrice || item.purchasePrice || 0);
      return [
        item.productCode || item.sku || "-",
        (item.productName || "Unnamed").substring(0, 18), 
        item.productType || "-",
        s,
        q,
        p.toLocaleString(),
        (q * p).toLocaleString()
      ];
    });

    // MASTER LEDGER TABLE
    // The margin bottom of 20 strictly stops the table from going past 190mm down the page
    // and forces any overflowing content onto a brand new Half-A4 page.
    autoTable(docPdf, {
      startY: ledgerStartY + 5,
      head: [["SKU", "Name", "Type", "Sold", "Cls.", "Price", "Total Val"]],
      body: ledgerRows.length > 0 ? ledgerRows : [["-", "No data matching filters", "-", "-", "-", "-", "-"]],
      theme: "grid",
      styles: { fontSize: 5, cellPadding: 1.5 }, 
      headStyles: { fillColor: [30, 41, 59] },
      margin: { top: 15, right: 5, bottom: 20, left: 5 }, // Strictest page break boundary
      rowPageBreak: 'avoid'
    });

    if (action === "preview") {
      const pdfBlobUrl = docPdf.output("bloburl");
      setPdfPreviewUrl(pdfBlobUrl);
    } else {
      docPdf.save(`Closing_Stock_Report_${storeName.replace(/\s+/g, '_')}.pdf`);
    }
  };

  const exportToExcel = () => {
    const workbook = XLSX.utils.book_new();
    
    const summaryData = [
      [`SHRUTI ERP - CLOSING STOCK REPORT`],
      [`Branch: ${storeName}`],
      [`Generated: ${new Date().toLocaleString()}`],
      [],
      ["Metric", "Value"],
      ["Unique SKUs", summaryStats.uniqueSKUCount],
      ["Total Sold", summaryStats.totalSold],
      ["Closing Stock Units", summaryStats.totalStock],
      ["Total Retail Valuation", summaryStats.totalValuation],
      [],
      ["Product Type", "Quantity", "Total Valuation"]
    ];
    
    summaryStats.typeSummaryArray.forEach(t => {
      summaryData.push([t.type, t.quantity, t.valuation]);
    });

    const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(workbook, summarySheet, "Summary Metrics");

    const ledgerRows = processedInventory.map(item => ({
      "SKU Code": item.productCode || item.sku,
      "Product Name": item.productName,
      "Product Type": item.productType,
      "Category": item.category,
      "Brand": item.brand,
      "Sold Quantity": item.soldQuantity || 0,
      "Closing Quantity": item.quantity || 0,
      "Unit Price": item.retailPrice || item.unitPrice || item.purchasePrice || 0,
      "Total Valuation": (Number(item.quantity) || 0) * (Number(item.retailPrice || item.unitPrice || item.purchasePrice || 0))
    }));
    
    const ledgerSheet = XLSX.utils.json_to_sheet(ledgerRows);
    XLSX.utils.book_append_sheet(workbook, ledgerSheet, "Stock Ledger");

    XLSX.writeFile(workbook, `Closing_Stock_Data_${storeName.replace(/\s+/g, '_')}.xlsx`);
  };

  const renderTableRows = (data) => (
    isDataLoading ? (
      <tr><td colSpan="7" className="p-8 text-center font-bold text-slate-400 uppercase tracking-wider">Loading Inventory Data...</td></tr>
    ) : data.length > 0 ? (
      data.map(p => {
        const q = Number(p.quantity) || 0;
        const uPrice = Number(p.retailPrice || p.unitPrice || p.purchasePrice || 0);
        return (
          <tr key={p.id} className="hover:bg-slate-50 transition-colors border-b border-slate-100">
            <td className="p-4 pl-6 font-mono font-bold text-slate-500">{p.productCode || p.sku || '-'}</td>
            <td className="p-4 font-bold text-slate-900">
              {p.productName || p.details || 'N/A'}
              <span className="block text-[9px] text-slate-400 uppercase mt-0.5">{p.brand}</span>
            </td>
            <td className="p-4 text-[10px] font-bold uppercase">
              <span className="text-slate-600 block">{p.productType || "Standard"}</span>
              <span className="text-slate-400 font-normal">{p.category || "General"}</span>
            </td>
            <td className="p-4 font-mono font-black text-center text-purple-600">{p.soldQuantity || 0}</td>
            <td className="p-4 font-mono font-black text-center text-blue-600">
              <span className={q === 0 ? "text-slate-300 line-through" : q < 5 ? "text-rose-600 bg-rose-50 px-2 py-1 rounded-md" : ""}>{q}</span>
            </td>
            <td className="p-4 font-mono font-bold text-right text-slate-500">₹{uPrice.toLocaleString()}</td>
            <td className="p-4 font-mono font-black text-emerald-600 text-right pr-6">₹{(q * uPrice).toLocaleString()}</td>
          </tr>
        );
      })
    ) : (
      <tr>
        <td colSpan="7" className="p-8 text-center text-slate-400 font-bold uppercase">No inventory records match your filters.</td>
      </tr>
    )
  );

  return (
    <div className="flex min-h-screen bg-slate-50 text-slate-800 font-sans antialiased selection:bg-blue-200">
      <StoreSidebar />

      <AnimatePresence>
        {pdfPreviewUrl && (
          <motion.div 
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/80 backdrop-blur-sm p-4 md:p-10"
          >
            <div className="bg-white w-full max-w-4xl h-full rounded-2xl shadow-2xl flex flex-col overflow-hidden">
              <div className="p-4 bg-slate-800 text-white flex items-center justify-between">
                <h3 className="font-bold flex items-center gap-2"><Eye size={18} /> Document Preview (A5 Portrait Format)</h3>
                <button onClick={() => setPdfPreviewUrl(null)} className="p-1 hover:bg-slate-700 rounded-md transition-colors">
                  <X size={20} />
                </button>
              </div>
              <div className="flex-1 w-full bg-slate-100">
                <iframe src={pdfPreviewUrl} className="w-full h-full border-none" title="PDF Preview" />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isViewAllOpen && (
          <motion.div 
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/80 backdrop-blur-sm p-4 md:p-10"
          >
            <div className="bg-white w-full max-w-6xl h-full rounded-2xl shadow-2xl flex flex-col overflow-hidden">
              <div className="p-4 bg-slate-800 text-white flex items-center justify-between">
                <h3 className="font-bold flex items-center gap-2"><Layers size={18} /> Complete Master Stock Ledger</h3>
                <button onClick={() => setIsViewAllOpen(false)} className="p-1 hover:bg-slate-700 rounded-md transition-colors">
                  <X size={20} />
                </button>
              </div>
              <div className="flex-1 w-full overflow-auto custom-scrollbar bg-slate-50">
                <table className="w-full text-left border-collapse text-[11px]">
                  <thead className="bg-white shadow-sm z-10 sticky top-0 font-bold text-slate-500 uppercase tracking-wider text-[10px]">
                    <tr className="border-b border-slate-200">
                      <th className="p-4 pl-6">SKU Code</th>
                      <th className="p-4">Item Details</th>
                      <th className="p-4">Type & Category</th>
                      <th className="p-4 text-center text-purple-600">Sold Qty</th>
                      <th className="p-4 text-center text-blue-600">Closing Stock</th>
                      <th className="p-4 text-right">Unit Price</th>
                      <th className="p-4 text-right pr-6 text-emerald-600">Total Value</th>
                    </tr>
                  </thead>
                  <tbody className="font-medium text-slate-700 bg-white">
                    {renderTableRows(processedInventory)}
                  </tbody>
                </table>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="flex-1 p-6 lg:p-10 space-y-6 overflow-x-hidden">
        
        {errorMessage && (
          <div className="bg-rose-50 border border-rose-200 p-4 rounded-xl text-rose-700 text-xs font-bold flex items-center justify-between">
            <span className="flex items-center gap-2"><AlertTriangle size={16} /> {errorMessage}</span>
          </div>
        )}

        <div className="bg-white border border-slate-200 p-6 rounded-3xl shadow-sm flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-2xl font-black uppercase text-slate-800 flex items-center gap-3">
              <Layers className="text-emerald-600" size={28} /> 
              Closing Stock Ledger
            </h1>
            <p className="text-slate-500 text-xs font-bold mt-1 tracking-wider uppercase">Branch: <span className="text-blue-600">{storeName || "Loading..."}</span></p>
          </div>
          
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative" ref={dropdownRef}>
              <button 
                onClick={() => setShowPdfMenu(!showPdfMenu)} 
                className="px-5 py-2.5 bg-rose-50 hover:bg-rose-600 text-rose-600 hover:text-white rounded-xl text-xs font-black uppercase tracking-wider transition-colors flex items-center gap-2 shadow-sm border border-rose-200"
              >
                <Download size={14}/> PDF Options <ChevronDown size={14} className={`transition-transform ${showPdfMenu ? "rotate-180" : ""}`}/>
              </button>
              
              <AnimatePresence>
                {showPdfMenu && (
                  <motion.div 
                    initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 10 }}
                    className="absolute right-0 mt-2 w-48 bg-white border border-slate-200 rounded-xl shadow-lg z-20 overflow-hidden"
                  >
                    <button onClick={() => handlePDF("preview")} className="w-full text-left px-4 py-3 text-xs font-bold text-slate-700 hover:bg-slate-50 flex items-center gap-2 border-b border-slate-100">
                      <Eye size={14} className="text-blue-500" /> Preview A5 PDF
                    </button>
                    <button onClick={() => handlePDF("download")} className="w-full text-left px-4 py-3 text-xs font-bold text-slate-700 hover:bg-slate-50 flex items-center gap-2">
                      <Download size={14} className="text-emerald-500" /> Download A5 PDF
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <button onClick={exportToExcel} className="px-5 py-2.5 bg-emerald-50 hover:bg-emerald-600 text-emerald-600 hover:text-white rounded-xl text-xs font-black uppercase tracking-wider transition-colors flex items-center gap-2 shadow-sm border border-emerald-200">
              <Download size={14}/> Excel File
            </button>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm space-y-4">
          <div className="flex items-center gap-2 border-b border-slate-100 pb-3">
            <Filter size={18} className="text-blue-500" />
            <h2 className="text-sm font-black uppercase tracking-wider text-slate-600">Stock Filter & Query Engine</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <div className="lg:col-span-2 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
              <input type="text" placeholder="Search Product Name or SKU..." value={search} onChange={e => setSearch(e.target.value)} className="w-full pl-9 pr-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold text-slate-700 outline-none focus:border-blue-500 transition-all"/>
            </div>

            <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs font-bold text-slate-600">
              <LayoutGrid size={14} className="text-slate-400"/>
              <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)} className="bg-transparent outline-none cursor-pointer w-full text-slate-800">
                <option value="">All Product Types</option>
                {uniqueTypes.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>

            <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs font-bold text-slate-600">
              <Tag size={14} className="text-slate-400"/>
              <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)} className="bg-transparent outline-none cursor-pointer w-full text-slate-800">
                <option value="">All Categories</option>
                {uniqueCategories.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>

            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-xl px-2 py-1 text-[10px] font-bold text-slate-600">
                <span className="w-8">Start:</span>
                <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="bg-transparent outline-none font-mono cursor-pointer w-full" />
              </div>
              <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-xl px-2 py-1 text-[10px] font-bold text-slate-600">
                <span className="w-8">End:</span>
                <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="bg-transparent outline-none font-mono cursor-pointer w-full" />
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          <div className="bg-white border border-slate-200 p-5 rounded-2xl shadow-sm flex flex-col justify-center">
            <div className="text-slate-400 text-[10px] font-black uppercase tracking-widest mb-1 flex items-center gap-2"><ShoppingCart size={14}/> Sold Stock (Filtered)</div>
            <div className="text-3xl font-black text-purple-600">{summaryStats.totalSold.toLocaleString()} <span className="text-xs font-bold text-slate-400">Units</span></div>
          </div>
          <div className="bg-white border border-slate-200 p-5 rounded-2xl shadow-sm flex flex-col justify-center">
            <div className="text-slate-400 text-[10px] font-black uppercase tracking-widest mb-1 flex items-center gap-2"><Package size={14}/> Closing Stock Balance</div>
            <div className="text-3xl font-black text-blue-600">{summaryStats.totalStock.toLocaleString()} <span className="text-xs font-bold text-slate-400">Units</span></div>
          </div>
          <div className="bg-white border border-slate-200 p-5 rounded-2xl shadow-sm flex flex-col justify-center">
            <div className="text-slate-400 text-[10px] font-black uppercase tracking-widest mb-1 flex items-center gap-2"><DollarSign size={14}/> Stock Valuation (Retail)</div>
            <div className="text-3xl font-black text-emerald-600">₹{summaryStats.totalValuation.toLocaleString()}</div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          <div className="lg:col-span-3 bg-white border border-slate-200 rounded-3xl p-6 shadow-sm flex flex-col overflow-hidden">
            <h3 className="text-xs font-black uppercase text-slate-800 mb-4 border-b border-slate-100 pb-3 flex items-center gap-2">
              <LayoutGrid size={16} className="text-blue-500" /> Product Type Summary
            </h3>
            <div className="overflow-x-auto flex-1 custom-scrollbar">
              <table className="w-full text-left border-collapse text-xs">
                <thead className="bg-slate-50 text-slate-500 uppercase tracking-wider sticky top-0 font-bold text-[10px]">
                  <tr className="border-b border-slate-200">
                    <th className="p-3 pl-4">Product Type</th>
                    <th className="p-3 text-center">Total Quantity</th>
                    <th className="p-3 text-right pr-4">Total Stock Value</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 font-medium">
                  {summaryStats.typeSummaryArray.map((row, i) => (
                    <tr key={i} className="hover:bg-slate-50">
                      <td className="p-3 pl-4 font-bold text-slate-700 uppercase">{row.type}</td>
                      <td className="p-3 font-mono font-black text-blue-600 text-center">{row.quantity.toLocaleString()}</td>
                      <td className="p-3 font-mono font-black text-emerald-600 text-right pr-4">₹{row.valuation.toLocaleString()}</td>
                    </tr>
                  ))}
                  {summaryStats.typeSummaryArray.length === 0 && (
                    <tr><td colSpan="3" className="p-6 text-center text-slate-400 font-bold uppercase">No Product Types Found</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="lg:col-span-2 bg-white border border-slate-200 p-6 rounded-3xl shadow-sm h-[350px] flex flex-col">
            <h3 className="text-xs font-black text-slate-800 uppercase tracking-wider mb-2 text-center border-b border-slate-100 pb-3">Stock Distribution by Type</h3>
            <div className="flex-1 w-full bg-white p-2 mt-2">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={summaryStats.chartDataArray} cx="50%" cy="50%" innerRadius={60} outerRadius={90} fill="#8884d8" paddingAngle={5} dataKey="value">
                    {summaryStats.chartDataArray.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />)}
                  </Pie>
                  <RechartsTooltip content={({ active, payload }) => {
                    if (active && payload && payload.length) {
                      return (
                        <div className="bg-slate-900 text-white p-3 rounded-lg shadow-xl border border-slate-700 text-xs font-bold">
                          <p className="mb-1 text-slate-300">{payload[0].name}</p>
                          <p className="text-blue-400 text-sm">Stock: {payload[0].value.toLocaleString()} Units</p>
                        </div>
                      );
                    }
                    return null;
                  }} />
                  <Legend wrapperStyle={{ fontSize: '10px', fontWeight: 'bold' }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-3xl shadow-sm flex flex-col overflow-hidden">
          <div className="p-5 border-b border-slate-100 bg-slate-50/50 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Package size={18} className="text-blue-600" />
              <h3 className="text-sm font-black uppercase text-slate-800">Master Stock Ledger (Showing Max 6 Items)</h3>
            </div>
          </div>
          
          <div className="overflow-x-auto text-[11px]">
            <table className="w-full text-left border-collapse">
              <thead className="bg-white shadow-sm z-10 font-bold text-slate-500 uppercase tracking-wider text-[10px]">
                <tr className="border-b border-slate-200">
                  <th className="p-4 pl-6">SKU Code</th>
                  <th className="p-4">Item Details</th>
                  <th className="p-4">Type & Category</th>
                  <th className="p-4 text-center text-purple-600">Sold Qty</th>
                  <th className="p-4 text-center text-blue-600">Closing Stock</th>
                  <th className="p-4 text-right">Unit Price</th>
                  <th className="p-4 text-right pr-6 text-emerald-600">Total Value</th>
                </tr>
              </thead>
              <tbody className="font-medium text-slate-700">
                {renderTableRows(displayData)}
              </tbody>
            </table>
          </div>

          {processedInventory.length > 6 && (
            <div className="bg-slate-50 p-5 border-t border-slate-200 flex items-center justify-center">
              <button 
                onClick={() => setIsViewAllOpen(true)}
                className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-black uppercase tracking-wider transition-colors shadow-sm flex items-center gap-2"
              >
                <Layers size={14} /> View All {processedInventory.length} Items
              </button>
            </div>
          )}
        </div>

      </div>
    </div>
  );
}